/**
 * Settings — Accessible to all users. Shows user tabs (Preferences,
 * Connections) for everyone, plus admin tabs (Business Rules, Users,
 * Query Intelligence, Usage) for admin/executive roles.
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import {
  ArrowLeft, RefreshCw, XCircle, Settings as SettingsIcon,
  DollarSign, Users, Link2, BarChart3, CheckCircle, AlertTriangle,
  Percent, Clock, CalendarDays, ShieldAlert, Building2,
  Mail, Landmark, CreditCard, ExternalLink, SlidersHorizontal, Save,
  Search, MessageSquare, Shield
} from 'lucide-react'

// Tabs visible to all users
const USER_TABS = [
  { key: 'preferences', label: 'Preferences', icon: SlidersHorizontal },
]

// Admin-only tabs
const ADMIN_TABS = [
  { key: 'connections', label: 'Connections', icon: Link2 },
  { key: 'rules', label: 'Business Rules', icon: DollarSign },
  { key: 'users', label: 'Users', icon: Users },
  { key: 'queries', label: 'Query Intelligence', icon: Search },
  { key: 'usage', label: 'Usage & Costs', icon: BarChart3 },
]

const ADMIN_ROLES = new Set(['admin', 'executive'])

/** Business rule definitions with defaults and formatting */
const BUSINESS_RULES = [
  { section: 'Domestic Rates', items: [
    { key: 'domestic_labor_rate', label: 'Domestic Labor Rate', default: '$40/hr', icon: DollarSign },
    { key: 'domestic_burden_rate', label: 'Domestic Burden Rate', default: '$100/hr', icon: DollarSign },
    { key: 'domestic_total_rate', label: 'Domestic Total Rate', default: '$140/hr', icon: DollarSign },
  ]},
  { section: 'Import Rates', items: [
    { key: 'import_labor_rate', label: 'Import Labor Rate', default: '$28/hr', icon: DollarSign },
    { key: 'import_burden_rate', label: 'Import Burden Rate', default: '$18/hr', icon: DollarSign },
    { key: 'import_total_rate', label: 'Import Total Rate', default: '$46/hr', icon: DollarSign },
  ]},
  { section: 'Thresholds & Alerts', items: [
    { key: 'target_gross_margin', label: 'Target Gross Margin', default: '45%', icon: Percent },
    { key: 'ar_past_due_days', label: 'AR Past Due Days', default: '10', icon: Clock },
    { key: 'quote_expiry_warning_days', label: 'Quote Expiry Warning Days', default: '7', icon: CalendarDays },
    { key: 'ap_due_warning_days', label: 'AP Due Warning Days', default: '7', icon: Clock },
    { key: 'margin_alert_threshold', label: 'Margin Alert Threshold', default: '10%', icon: ShieldAlert },
  ]},
]

export default function Settings() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = ADMIN_ROLES.has(user?.role)
  const [activeTab, setActiveTab] = useState('preferences')
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState([])
  const [userDetails, setUserDetails] = useState(null)
  const [queryIntel, setQueryIntel] = useState(null)
  const [healthData, setHealthData] = useState(null)
  const [m365Status, setM365Status] = useState(null)
  const [plaidUsage, setPlaidUsage] = useState(null)
  const [adminDataLoaded, setAdminDataLoaded] = useState(false)

  // Build tab list based on role
  const tabs = isAdmin ? [...USER_TABS, ...ADMIN_TABS] : USER_TABS

  // Fetch connection status for all users
  const fetchUserData = useCallback(async () => {
    const token = localStorage.getItem('amtrend_token')
    const headers = { Authorization: `Bearer ${token}` }
    try {
      const results = await Promise.allSettled([
        fetch('/health').then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/auth/m365/status', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ])
      if (results[0].status === 'fulfilled') setHealthData(results[0].value)
      if (results[1].status === 'fulfilled') setM365Status(results[1].value)
    } catch (err) {
      console.error('Failed to fetch connection data:', err)
    }
  }, [])

  // Fetch admin-only data (lazy — only when admin tab first accessed)
  const fetchAdminData = useCallback(async () => {
    if (adminDataLoaded) return
    setLoading(true)
    const token = localStorage.getItem('amtrend_token')
    const headers = { Authorization: `Bearer ${token}` }
    try {
      const results = await Promise.allSettled([
        fetch('/api/admin/stats', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/admin/plaid-usage', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/admin/users/detail', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        fetch('/api/admin/query-intelligence?period=30d', { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      ])
      if (results[0].status === 'fulfilled' && results[0].value) {
        const userData = results[0].value?.users
        setUsers(userData?.list || userData || [])
      }
      if (results[1].status === 'fulfilled') setPlaidUsage(results[1].value)
      if (results[2].status === 'fulfilled') setUserDetails(results[2].value)
      if (results[3].status === 'fulfilled') setQueryIntel(results[3].value)
      setAdminDataLoaded(true)
    } catch (err) {
      console.error('Failed to fetch admin data:', err)
    } finally {
      setLoading(false)
    }
  }, [adminDataLoaded])

  // Load connection data on mount only for admins
  useEffect(() => { if (isAdmin) fetchUserData() }, [])

  // Lazy-load admin data when switching to admin tab
  useEffect(() => {
    if (isAdmin && ['connections', 'rules', 'users', 'queries', 'usage'].includes(activeTab)) {
      fetchAdminData()
      if (activeTab === 'connections') fetchUserData()
    }
  }, [activeTab, isAdmin, fetchAdminData])

  // Redirect to /admin when Usage tab is selected
  useEffect(() => {
    if (activeTab === 'usage') navigate('/admin')
  }, [activeTab, navigate])

  const token = localStorage.getItem('amtrend_token')
  const displayName = (user?.username || 'User').split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-surface-dark">
      {/* Header */}
      <header className="bg-white dark:bg-surface-dark-raised border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Back to Trendy"
              >
                <ArrowLeft size={20} className="text-gray-700 dark:text-gray-300" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <SettingsIcon size={20} className="text-gray-500 dark:text-gray-400" />
                  Settings
                </h1>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {displayName} &middot; {user?.department || user?.role_label || 'Team'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-purple-700 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors"
                >
                  <Shield size={14} />
                  Admin Dashboard
                </button>
              )}
              <button
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amtrend-blue bg-amtrend-blue/10 rounded-lg hover:bg-amtrend-blue/20 transition-colors"
              >
                <MessageSquare size={14} />
                Back to Trendy
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Bar */}
      <div className="bg-white dark:bg-surface-dark-raised border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 py-2 overflow-x-auto">
            {tabs.map((tab, i) => {
              const Icon = tab.icon
              const isAdminTab = ADMIN_TABS.some(t => t.key === tab.key)
              // Show separator before admin section
              const showDivider = isAdmin && i === USER_TABS.length
              return (
                <div key={tab.key} className="flex items-center">
                  {showDivider && (
                    <div className="w-px h-6 bg-gray-200 dark:bg-gray-700 mx-2 flex-shrink-0" />
                  )}
                  <button
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors whitespace-nowrap ${
                      activeTab === tab.key
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-medium'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon size={15} />
                    {tab.label}
                    {isAdminTab && (
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 flex-shrink-0" title="Admin only" />
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        )}
        {!loading && activeTab === 'preferences' && <PreferencesTab />}
        {!loading && activeTab === 'connections' && isAdmin && (
          <ConnectionsTab
            healthData={healthData}
            m365Status={m365Status}
            plaidUsage={plaidUsage}
          />
        )}
        {!loading && activeTab === 'rules' && isAdmin && <BusinessRulesTab />}
        {!loading && activeTab === 'users' && isAdmin && <UserManagementTab users={users} userDetails={userDetails} />}
        {!loading && activeTab === 'queries' && isAdmin && <QueryIntelligenceTab data={queryIntel} token={token} />}
      </main>
    </div>
  )
}

/* ─── Helpers ──────────────────────────────────────────────────────────────── */

/** Convert "first.last" or "firstlast" to "First Last" */
function formatDisplayName(username) {
  if (!username) return 'Unknown'
  return username
    .split('.')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

/** Relative time string like "2 days ago" */
function timeAgo(dateStr) {
  if (!dateStr) return 'Never'
  const now = new Date()
  const d = new Date(dateStr)
  const diffMs = now - d
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHr = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay > 30) return `${Math.floor(diffDay / 30)}mo ago`
  if (diffDay > 0) return `${diffDay}d ago`
  if (diffHr > 0) return `${diffHr}h ago`
  if (diffMin > 0) return `${diffMin}m ago`
  return 'Just now'
}

/** Role badge color mapping */
function roleBadgeClasses(role) {
  switch (role) {
    case 'admin': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
    case 'executive': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    case 'plant_manager': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    case 'team': return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }
}

/** Avatar background color by role */
function avatarColorClasses(role) {
  switch (role) {
    case 'admin': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300'
    case 'executive': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
    case 'plant_manager': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }
}

/* ─── Tab 1: Business Rules (Editable) ───────────────────────────────────── */

function BusinessRulesTab() {
  const [rules, setRules] = useState({})
  const [defaults, setDefaults] = useState({})
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState(null)
  const [loaded, setLoaded] = useState(false)

  // Load current values from backend
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('amtrend_token')
        const res = await fetch('/api/admin/settings/business-rules', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          setRules(data.rules || {})
          setDefaults(data.defaults || {})
        }
      } catch (err) {
        console.error('Failed to load business rules:', err)
      } finally {
        setLoaded(true)
      }
    })()
  }, [])

  const handleChange = (key, value) => {
    setRules(prev => ({ ...prev, [key]: value }))
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/admin/settings/business-rules', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ rules })
      })
      const data = await res.json()
      if (res.ok) {
        setSaveResult({ success: true, message: data.message || 'Saved' })
        setEditing(false)
      } else {
        setSaveResult({ success: false, message: data.detail || 'Save failed' })
      }
    } catch (err) {
      setSaveResult({ success: false, message: err.message })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = (key) => {
    setRules(prev => ({ ...prev, [key]: defaults[key] }))
  }

  /** Format a value for display based on its key */
  const formatValue = (key, val) => {
    if (val === undefined || val === null) return '-'
    if (key.includes('margin') || key.includes('threshold')) return `${(Number(val) * 100).toFixed(0)}%`
    if (key.includes('rate')) return `$${val}/hr`
    if (key.includes('days')) return `${val} days`
    return String(val)
  }

  /** Parse input back to storage format */
  const parseInput = (key, inputVal) => {
    const num = parseFloat(inputVal)
    if (isNaN(num)) return inputVal
    if (key.includes('margin') || key.includes('threshold')) return num / 100
    return num
  }

  /** Get raw number for input field */
  const getInputValue = (key, val) => {
    if (val === undefined || val === null) return ''
    if (key.includes('margin') || key.includes('threshold')) return (Number(val) * 100).toFixed(0)
    return String(val)
  }

  const isOverridden = (key) => rules[key] !== undefined && rules[key] !== defaults[key]

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Business Rules</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Configuration values used across reporting and alerts.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveResult && (
            <span className={`text-xs px-3 py-1.5 rounded-lg ${
              saveResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {saveResult.message}
            </span>
          )}
          {editing ? (
            <>
              <button
                onClick={() => { setEditing(false); setSaveResult(null) }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <RefreshCw size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                Save Changes
              </button>
            </>
          ) : (
            <button
              onClick={() => { setEditing(true); setSaveResult(null) }}
              className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2"
            >
              <SettingsIcon size={14} />
              Edit
            </button>
          )}
        </div>
      </div>

      {BUSINESS_RULES.map(section => (
        <div key={section.section}>
          <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
            {section.section}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {section.items.map(rule => {
              const Icon = rule.icon
              const currentVal = rules[rule.key]
              const overridden = isOverridden(rule.key)
              return (
                <div
                  key={rule.key}
                  className={`bg-white dark:bg-surface-dark-raised rounded-xl border p-5 flex items-center gap-4 transition-colors ${
                    overridden ? 'border-blue-200 bg-blue-50/30 dark:border-blue-800' : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    overridden ? 'bg-blue-100 dark:bg-blue-900/40' : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <Icon size={18} className={overridden ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{rule.label}</p>
                    {editing ? (
                      <div className="flex items-center gap-2 mt-1">
                        <input
                          type="number"
                          value={getInputValue(rule.key, currentVal)}
                          onChange={(e) => handleChange(rule.key, parseInput(rule.key, e.target.value))}
                          className="w-24 px-2 py-1 text-sm font-semibold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                        />
                        <span className="text-xs text-gray-400">
                          {rule.key.includes('rate') ? '/hr' : rule.key.includes('days') ? 'days' : rule.key.includes('margin') || rule.key.includes('threshold') ? '%' : ''}
                        </span>
                        {overridden && (
                          <button
                            onClick={() => handleReset(rule.key)}
                            className="text-xs text-gray-400 hover:text-gray-600 underline"
                            title={`Reset to default: ${formatValue(rule.key, defaults[rule.key])}`}
                          >
                            reset
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          {loaded ? formatValue(rule.key, currentVal) : rule.default}
                        </p>
                        {overridden && (
                          <span className="text-[10px] text-blue-600 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded font-medium">
                            custom
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ─── Tab 2: User Management (Rich Card Dashboard) ─────────────────────── */

function UserManagementTab({ users, userDetails }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [deptFilter, setDeptFilter] = useState('all')
  const [sortBy, setSortBy] = useState('last_login')

  // Merge basic users with detail data
  const enrichedUsers = (users || []).map(user => {
    const detail = userDetails?.users?.find(d => d.username === user.username) || {}
    return { ...user, ...detail }
  })

  // Collect unique departments
  const departments = [...new Set(enrichedUsers.map(u => u.department).filter(Boolean))]

  // Filter
  const filtered = enrichedUsers.filter(user => {
    const matchesSearch = !searchQuery ||
      (user.username || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      formatDisplayName(user.username).toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.department || '').toLowerCase().includes(searchQuery.toLowerCase())
    const matchesDept = deptFilter === 'all' || user.department === deptFilter
    return matchesSearch && matchesDept
  })

  // Sort
  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'last_login': {
        const da = a.last_login ? new Date(a.last_login) : new Date(0)
        const db = b.last_login ? new Date(b.last_login) : new Date(0)
        return db - da
      }
      case 'messages':
        return (b.messages_last_30d || b.total_messages || 0) - (a.messages_last_30d || a.total_messages || 0)
      case 'cost':
        return (b.cost_last_30d || b.total_cost_usd || 0) - (a.cost_last_30d || a.total_cost_usd || 0)
      default:
        return 0
    }
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">User Management</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {users.length} registered user{users.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Filters Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or department..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-surface-dark-raised text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        {/* Department Filter */}
        <select
          value={deptFilter}
          onChange={e => setDeptFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-surface-dark-raised text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Departments</option>
          {departments.map(dept => (
            <option key={dept} value={dept}>{dept}</option>
          ))}
        </select>
        {/* Sort */}
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-surface-dark-raised text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="last_login">Sort: Last Login</option>
          <option value="messages">Sort: Messages</option>
          <option value="cost">Sort: Cost</option>
        </select>
      </div>

      {/* User Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sorted.map((user, i) => (
          <UserCard key={user.id || user.username || i} user={user} />
        ))}
        {sorted.length === 0 && (
          <div className="col-span-full text-center py-12 text-sm text-gray-400 dark:text-gray-500">
            No users match your filters
          </div>
        )}
      </div>
    </div>
  )
}

function UserCard({ user }) {
  const displayName = formatDisplayName(user.username)
  const initial = (user.username || '?')[0].toUpperCase()
  const topTools = (user.most_used_tools || user.top_tools || []).slice(0, 3)

  return (
    <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5 flex flex-col gap-4 transition-colors hover:border-gray-300 dark:hover:border-gray-600">
      {/* Top row: avatar, name, badges */}
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColorClasses(user.role)}`}>
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{displayName}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.username || 'unknown'}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full ${roleBadgeClasses(user.role)}`}>
            {(user.role || 'user').replace('_', ' ')}
          </span>
          {user.department && (
            <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              {user.department}
            </span>
          )}
        </div>
      </div>

      {/* Last Login */}
      <div className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <Clock size={12} />
        <span>Last login: {timeAgo(user.last_login)}</span>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-2 text-center">
        <StatCell label="Sessions" value={user.total_sessions ?? '-'} />
        <StatCell label="Messages" value={user.messages_last_30d ?? user.total_messages ?? '-'} />
        <StatCell label="Cost" value={user.cost_last_30d != null ? `$${Number(user.cost_last_30d).toFixed(2)}` : user.total_cost_usd != null ? `$${Number(user.total_cost_usd).toFixed(2)}` : '-'} />
        <StatCell label="Feedback" value={
          (user.thumbs_up_count != null && user.thumbs_down_count != null)
            ? `${user.thumbs_up_count}👍 ${user.thumbs_down_count}👎`
            : '-'
        } />
      </div>

      {/* Top Tools */}
      {topTools.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {topTools.map((tool, idx) => (
            <span key={idx} className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
              {typeof tool === 'string' ? tool : tool.name || tool.tool || 'tool'}
            </span>
          ))}
        </div>
      )}

      {/* Last Query */}
      {user.last_query && (
        <p className="text-xs text-gray-400 dark:text-gray-500 italic truncate" title={user.last_query}>
          "{user.last_query}"
        </p>
      )}
    </div>
  )
}

function StatCell({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs font-semibold text-gray-900 dark:text-gray-100">{value}</span>
      <span className="text-[10px] text-gray-400 dark:text-gray-500">{label}</span>
    </div>
  )
}

/* ─── Tab 2.5: Query Intelligence ──────────────────────────────────────────── */

function QueryIntelligenceTab({ data, token }) {
  const [period, setPeriod] = useState('30d')
  const [intelData, setIntelData] = useState(data)
  const [loading, setLoading] = useState(false)
  const [querySearch, setQuerySearch] = useState('')

  // Re-fetch when period changes
  useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await fetch(`/api/admin/query-intelligence?period=${period}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok && !cancelled) {
          const result = await res.json()
          setIntelData(result)
        }
      } catch (err) {
        console.error('Failed to fetch query intelligence:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [period, token])

  // Also update when parent data arrives
  useEffect(() => {
    if (data && !intelData) setIntelData(data)
  }, [data])

  const queries = intelData?.queries || intelData?.recent_queries || []
  const dailyVolume = intelData?.daily_volume || []
  const toolPopularity = intelData?.tool_popularity || []
  const categories = intelData?.categories || intelData?.query_categories || []

  // Filter queries by search
  const filteredQueries = queries.filter(q => {
    if (!querySearch) return true
    const text = (q.query || q.text || '').toLowerCase()
    const user = (q.username || q.user || '').toLowerCase()
    return text.includes(querySearch.toLowerCase()) || user.includes(querySearch.toLowerCase())
  })

  const maxToolCount = toolPopularity.length > 0 ? Math.max(...toolPopularity.map(t => t.count || 0), 1) : 1
  const maxDailyCount = dailyVolume.length > 0 ? Math.max(...dailyVolume.map(d => d.count || 0), 1) : 1

  return (
    <div className="space-y-8">
      {/* Header with period selector */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Query Intelligence</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Analyze user queries, tool usage, and activity patterns.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {loading && <RefreshCw size={14} className="animate-spin text-blue-500" />}
          <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
            {['7d', '30d', '90d'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  period === p
                    ? 'bg-white dark:bg-surface-dark-raised text-gray-900 dark:text-gray-100 shadow-sm'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Section A: Recent Queries Feed */}
      <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Queries</h3>
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search queries..."
              value={querySearch}
              onChange={e => setQuerySearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-gray-50 dark:divide-gray-700/50">
          {filteredQueries.length > 0 ? filteredQueries.slice(0, 100).map((q, idx) => (
            <div key={idx} className="px-5 py-3 flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${avatarColorClasses(q.role)}`}>
                {((q.username || q.user || '?')[0] || '?').toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{q.username || q.user || 'unknown'}</span>
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">{timeAgo(q.timestamp || q.created_at)}</span>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-300 truncate">{q.query || q.text || '-'}</p>
                {(q.tools || q.tools_used || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(q.tools || q.tools_used || []).slice(0, 5).map((tool, ti) => (
                      <span key={ti} className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium rounded bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
                        {typeof tool === 'string' ? tool : tool.name || 'tool'}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )) : (
            <div className="px-5 py-8 text-center text-xs text-gray-400 dark:text-gray-500">
              {querySearch ? 'No queries match your search' : 'No query data available'}
            </div>
          )}
        </div>
      </div>

      {/* Bottom 3-col grid: Volume, Tool Popularity, Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Section B: Query Volume Chart */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Daily Query Volume</h3>
          {dailyVolume.length > 0 ? (
            <div className="flex items-end gap-[3px] h-36">
              {dailyVolume.map((day, idx) => {
                const height = Math.max((day.count / maxDailyCount) * 100, 4)
                return (
                  <div
                    key={idx}
                    className="flex-1 group relative"
                    title={`${day.date || day.day || ''}: ${day.count} queries`}
                  >
                    <div
                      className="w-full bg-blue-500 dark:bg-blue-400 rounded-t-sm opacity-80 hover:opacity-100 transition-opacity"
                      style={{ height: `${height}%` }}
                    />
                    {/* Tooltip on hover */}
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 hidden group-hover:block bg-gray-900 dark:bg-gray-700 text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap z-10">
                      {day.count}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="h-36 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
              No volume data available
            </div>
          )}
          {dailyVolume.length > 0 && (
            <div className="flex justify-between mt-2 text-[10px] text-gray-400">
              <span>{dailyVolume[0]?.date || dailyVolume[0]?.day || ''}</span>
              <span>{dailyVolume[dailyVolume.length - 1]?.date || dailyVolume[dailyVolume.length - 1]?.day || ''}</span>
            </div>
          )}
        </div>

        {/* Section C: Tool Popularity */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Tool Popularity</h3>
          {toolPopularity.length > 0 ? (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {toolPopularity.slice(0, 15).map((tool, idx) => {
                const pct = ((tool.count || 0) / maxToolCount) * 100
                return (
                  <div key={idx} className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-600 dark:text-gray-300 w-28 truncate flex-shrink-0" title={tool.name || tool.tool}>
                      {tool.name || tool.tool || 'unknown'}
                    </span>
                    <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 dark:bg-emerald-400 rounded-full transition-all"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 w-8 text-right flex-shrink-0">
                      {tool.count || 0}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className="h-36 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
              No tool usage data available
            </div>
          )}
        </div>

        {/* Section D: Query Categories */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-5">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4">Query Categories</h3>
          {categories.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {categories.map((cat, idx) => {
                const name = cat.name || cat.category || cat.intent || 'unknown'
                const count = cat.count || 0
                // Size the badge based on count
                const maxCat = Math.max(...categories.map(c => c.count || 0), 1)
                const intensity = Math.min(Math.max(count / maxCat, 0.3), 1)
                return (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 dark:border-gray-600 px-2.5 py-1 transition-colors"
                    style={{
                      backgroundColor: `rgba(99, 102, 241, ${intensity * 0.15})`,
                      color: intensity > 0.6 ? 'rgb(79, 70, 229)' : undefined,
                    }}
                  >
                    <span className={`text-xs font-medium ${intensity <= 0.6 ? 'text-gray-700 dark:text-gray-300' : 'dark:text-indigo-300'}`}>
                      {name}
                    </span>
                    <span className="text-[10px] text-gray-400 dark:text-gray-500">{count}</span>
                  </span>
                )
              })}
            </div>
          ) : (
            <div className="h-36 flex items-center justify-center text-xs text-gray-400 dark:text-gray-500">
              No category data available
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ─── Tab 3: Connections ─────────────────────────────────────────────────── */

function ConnectionsTab({ healthData, m365Status, plaidUsage }) {
  const epicorConnected = healthData?.status === 'healthy' || !!healthData
  const epicorUrl = healthData?.epicor_url || healthData?.services?.epicor?.url || null
  const epicorLatency = healthData?.response_time_ms || healthData?.services?.epicor?.latency_ms || null
  const epicorLastCheck = healthData?.checked_at || healthData?.timestamp || null

  const m365Connected = m365Status?.connected === true || m365Status?.status === 'connected'
  const m365Expired = m365Status?.is_expired === true
  const m365ExpiresAt = m365Status?.expires_at
  const m365HasRefresh = m365Status?.has_refresh_token

  const plaidConfigured = plaidUsage?.configured === true || (plaidUsage?.institutions && plaidUsage.institutions.length > 0)
  const plaidInstitutions = plaidUsage?.institutions || []
  const plaidLinkedItems = plaidUsage?.linked_items || plaidUsage?.items_count || 0
  const plaidLastSync = plaidUsage?.last_sync || plaidUsage?.last_transaction_sync || null
  const plaidAccounts = plaidUsage?.accounts || []

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Connections</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Integration health and status for all connected services.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Epicor ERP */}
        <ConnectionCard
          icon={Building2}
          name="Epicor ERP"
          description="Enterprise resource planning"
          connected={epicorConnected}
          details={[
            epicorUrl ? `URL: ${epicorUrl}` : null,
            healthData?.version ? `Version: ${healthData.version}` : null,
            epicorLatency ? `Latency: ${epicorLatency}ms` : null,
            epicorLastCheck ? `Last check: ${new Date(epicorLastCheck).toLocaleString()}` : null,
          ].filter(Boolean)}
        />

        {/* Microsoft 365 */}
        <ConnectionCard
          icon={Mail}
          name="Microsoft 365"
          description="Email, calendar, and files"
          connected={m365Connected && !m365Expired}
          warning={m365Expired ? 'Token expired — users need to reconnect' : null}
          details={[
            m365Status?.user ? `User: ${m365Status.user}` : null,
            m365Status?.tenant ? `Tenant: ${m365Status.tenant}` : null,
            m365ExpiresAt ? `Token expires: ${new Date(m365ExpiresAt).toLocaleString()}` : null,
            m365HasRefresh != null ? `Auto-refresh: ${m365HasRefresh ? 'Yes' : 'No'}` : null,
            m365Status?.scopes?.length ? `Scopes: ${m365Status.scopes.length} granted` : null,
          ].filter(Boolean)}
        />

        {/* Plaid Banking */}
        <ConnectionCard
          icon={Landmark}
          name="Plaid Banking"
          description="Bank accounts and transactions"
          connected={plaidConfigured}
          details={[
            plaidInstitutions.length > 0 ? `Institutions: ${plaidInstitutions.join(', ')}` : null,
            plaidLinkedItems ? `Linked items: ${plaidLinkedItems}` : null,
            plaidAccounts.length > 0 ? `Accounts: ${plaidAccounts.map(a => `${a.name || a.official_name} (${a.subtype || a.type})`).join(', ')}` : null,
            plaidLastSync ? `Last sync: ${new Date(plaidLastSync).toLocaleString()}` : null,
          ].filter(Boolean)}
        />
      </div>
    </div>
  )
}

/* ─── Tab 4: Preferences ──────────────────────────────────────────────── */

const TIMEZONES = [
  { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
  { value: 'America/Denver', label: 'Mountain Time (Denver)' },
  { value: 'America/Chicago', label: 'Central Time (Chicago)' },
  { value: 'America/New_York', label: 'Eastern Time (New York)' },
]

const DASHBOARD_PERIODS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: '90_days', label: '90 Days' },
  { value: 'all_time', label: 'All Time' },
]

const DETAIL_LEVELS = ['Summary', 'Detailed', 'Full']

function PreferencesTab() {
  const [prefs, setPrefs] = useState({
    timezone: '',
    default_institution: '',
    report_detail_level: 'Summary',
    default_dashboard_period: 'this_month',
  })
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveResult, setSaveResult] = useState(null)

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem('amtrend_token')
        const res = await fetch('/api/preferences', {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.ok) {
          const data = await res.json()
          if (data.preferences && Object.keys(data.preferences).length > 0) {
            setPrefs(prev => ({ ...prev, ...data.preferences }))
          }
        }
      } catch (err) {
        console.error('Failed to load preferences:', err)
      } finally {
        setLoaded(true)
      }
    })()
  }, [])

  const handleSave = async () => {
    setSaving(true)
    setSaveResult(null)
    try {
      const token = localStorage.getItem('amtrend_token')
      const res = await fetch('/api/preferences', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: prefs })
      })
      const data = await res.json()
      if (res.ok) {
        setSaveResult({ success: true, message: 'Preferences saved' })
      } else {
        setSaveResult({ success: false, message: data.detail || 'Save failed' })
      }
    } catch (err) {
      setSaveResult({ success: false, message: err.message })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Preferences</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Personal settings that customize your Command Center experience.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {saveResult && (
            <span className={`text-xs px-3 py-1.5 rounded-lg ${
              saveResult.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {saveResult.message}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />}
            Save Preferences
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Timezone */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Timezone</label>
          <select
            value={prefs.timezone}
            onChange={e => setPrefs(prev => ({ ...prev, timezone: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            <option value="">Select timezone...</option>
            {TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-2">Used for scheduled reports and date formatting.</p>
        </div>

        {/* Default Bank Institution */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Bank Institution</label>
          <input
            type="text"
            value={prefs.default_institution}
            onChange={e => setPrefs(prev => ({ ...prev, default_institution: e.target.value }))}
            placeholder="e.g., Chase, Bank of America"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
          <p className="text-xs text-gray-400 mt-2">Default institution for banking queries.</p>
        </div>

        {/* Report Detail Level */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Report Detail Level</label>
          <div className="flex gap-4">
            {DETAIL_LEVELS.map(level => (
              <label key={level} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="detail_level"
                  value={level}
                  checked={prefs.report_detail_level === level}
                  onChange={() => setPrefs(prev => ({ ...prev, report_detail_level: level }))}
                  className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{level}</span>
              </label>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3">Controls how much detail playbook reports include.</p>
        </div>

        {/* Default Dashboard Period */}
        <div className="bg-white dark:bg-surface-dark-raised rounded-xl border border-gray-200 dark:border-gray-700 p-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Dashboard Period</label>
          <select
            value={prefs.default_dashboard_period}
            onChange={e => setPrefs(prev => ({ ...prev, default_dashboard_period: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          >
            {DASHBOARD_PERIODS.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
          <p className="text-xs text-gray-400 mt-2">Default time period for dashboard analytics views.</p>
        </div>
      </div>
    </div>
  )
}

function ConnectionCard({ icon: Icon, name, description, connected, warning, details }) {
  const hasWarning = !!warning
  return (
    <div className={`bg-white dark:bg-surface-dark-raised rounded-xl border p-6 ${
      hasWarning ? 'border-amber-300 dark:border-amber-600' : 'border-gray-200 dark:border-gray-700'
    }`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            hasWarning ? 'bg-amber-100 dark:bg-amber-900/40' :
            connected ? 'bg-emerald-100 dark:bg-emerald-900/40' : 'bg-gray-100 dark:bg-gray-700'
          }`}>
            <Icon size={20} className={
              hasWarning ? 'text-amber-600 dark:text-amber-400' :
              connected ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-400'
            } />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{name}</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
          </div>
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
          hasWarning
            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
            : connected
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
              : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
        }`}>
          {hasWarning ? <AlertTriangle size={12} /> : connected ? <CheckCircle size={12} /> : <XCircle size={12} />}
          {hasWarning ? 'Warning' : connected ? 'Connected' : 'Not Configured'}
        </span>
      </div>
      {hasWarning && (
        <div className="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <p className="text-xs text-amber-700 dark:text-amber-300 font-medium">{warning}</p>
        </div>
      )}
      {details.length > 0 && (
        <div className="space-y-1 pt-3 border-t border-gray-100 dark:border-gray-700">
          {details.map((detail, i) => (
            <p key={i} className="text-xs text-gray-500 dark:text-gray-400 truncate">{detail}</p>
          ))}
        </div>
      )}
    </div>
  )
}
